import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { COUNTRY_CODES, LANGUAGES, PHONE_CODES, sofVisible, todayIn, type Profile } from '@platform/shared';
import { DateField } from '../../components/DateRange';
import { Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { navigate } from '../../lib/router';
import { useCompliance, useLoad } from '../../stores/account';
import { loadMe, useMe } from '../../stores/session';
import { AIcon, Card, errMsg, Field, H1, H2, Notice, PillButton, PillLink, Segmented } from './kit';
export { KycTab } from './Kyc';
export { SecurityTab } from './Security';
export { SourceOfFundsTab } from './SourceOfFunds';

type Tab = 'details' | 'security' | 'kyc' | 'sof';
const TAB_PATH: Record<Tab, string> = { details: '/account/personal', security: '/account/security', kyc: '/account/kyc', sof: '/account/source-of-funds' };

export function PersonalTabs({ tab, children }: { tab: Tab; children: ReactNode }) {
  // Source of funds stays out of sight until the client's deposits reach the threshold.
  const compliance = useCompliance();
  const showSof = tab === 'sof' || (!!compliance && sofVisible(compliance));
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <H1>Personal information</H1>
        <Segmented
          label="Personal information"
          className="w-full sm:w-auto"
          options={[
            { id: 'details', label: 'Details' },
            { id: 'security', label: 'Security' },
            { id: 'kyc', label: 'KYC' },
            ...(showSof ? [{ id: 'sof' as const, label: 'Source of funds' }] : []),
          ]}
          value={tab}
          onChange={(t) => navigate(TAB_PATH[t])}
        />
      </div>
      {children}
    </div>
  );
}

const countryNames = (() => {
  let dn: Intl.DisplayNames | null = null;
  try {
    dn = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    /* old browser */
  }
  return COUNTRY_CODES.map((c) => ({ code: c, name: dn?.of(c) ?? c })).sort((a, b) => a.name.localeCompare(b.name));
})();

const timezones = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return ['UTC'];
  }
})();

type Form = Record<'firstName' | 'lastName' | 'dob' | 'phoneCc' | 'phone' | 'country' | 'addressLine' | 'city' | 'postalCode' | 'language' | 'timezone', string>;
const toForm = (p: Profile): Form => ({
  firstName: p.firstName ?? '',
  lastName: p.lastName ?? '',
  dob: p.dob ?? '',
  phoneCc: p.phoneCc ?? '',
  phone: p.phone ?? '',
  country: p.country ?? '',
  addressLine: p.addressLine ?? '',
  city: p.city ?? '',
  postalCode: p.postalCode ?? '',
  language: p.language,
  timezone: p.timezone,
});

function EmailChange({ current, onDone }: { current: string; onDone: (p: Profile) => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<{ challengeId: string; devCode?: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reset = () => {
    setOpen(false);
    setEmail('');
    setPassword('');
    setChallenge(null);
    setCode('');
    setErr(null);
  };
  const start = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      setChallenge(await api.post('/api/profile/email/start', { newEmail: email, password }));
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const p = await api.post<Profile>('/api/profile/email/confirm', { challengeId: challenge!.challengeId, code });
      reset();
      onDone(p);
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label="Email" htmlFor="pi-email" className="md:col-span-2" hint={open ? undefined : 'Changing it needs your password and a code sent to the new address.'}>
      <div className="flex gap-3">
        <input id="pi-email" className="field" readOnly value={current} />
        {!open && (
          <PillButton kind="secondary" onClick={() => setOpen(true)} className="shrink-0">
            Change
          </PillButton>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-5 rounded-[20px] bg-track p-5 md:p-6">
          {!challenge ? (
            <form onSubmit={start} className="grid gap-4 md:grid-cols-2">
              <Field label="New email" htmlFor="pi-new-email">
                <input id="pi-new-email" type="email" required autoComplete="email" className="field" value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              <Field label="Current password" htmlFor="pi-email-pw">
                <input id="pi-email-pw" type="password" required autoComplete="current-password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} />
              </Field>
              <div className="flex gap-3 md:col-span-2">
                <PillButton type="submit" busy={busy}>
                  Send code
                </PillButton>
                <PillButton kind="secondary" onClick={reset}>
                  Cancel
                </PillButton>
              </div>
            </form>
          ) : (
            <form onSubmit={confirm} className="flex flex-col gap-3">
              <span className="text-[15px] text-muted">
                Enter the 6-digit code we sent to <b className="text-fg">{email}</b>.
              </span>
              {challenge.devCode && <span className="text-[13px] text-accent-fg">Dev mode (no SMTP): your code is {challenge.devCode}</span>}
              <div className="flex flex-wrap items-center gap-3">
                <input
                  aria-label="Code"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="field num !w-44 text-center tracking-[.3em]"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <PillButton type="submit" busy={busy} disabled={code.length !== 6}>
                  Confirm email
                </PillButton>
                <PillButton kind="secondary" onClick={reset}>
                  Cancel
                </PillButton>
              </div>
            </form>
          )}
          {err && <p className="m-0 text-[14px] text-down" role="alert">{err}</p>}
        </div>
      )}
    </Field>
  );
}

/**
 * A staff session from the CRM whose staff member may not see the client's email and phone
 * (STAFF_CONTACT_HIDDEN_PLAN.md): the server sends them masked; shown read-only, marked as hidden.
 */
function HiddenContact({ id, label, value, hint, className }: { id: string; label: string; value: string; hint?: string; className?: string }) {
  return (
    <Field label={<>{label} <AIcon name="lock" size={13} className="text-muted" /></>} htmlFor={id} className={className} hint={hint}>
      <div className="relative">
        <input id={id} className="field num !pr-44" readOnly value={value || '—'} />
        <span className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 rounded-full bg-managed-bg px-2.5 py-0.5 text-[12px] font-semibold text-managed">
          Hidden in staff view
        </span>
      </div>
    </Field>
  );
}

export function DetailsTab() {
  const contactHidden = !!useMe()?.contactHidden;
  const { data: profile, error, setData } = useLoad<Profile>('/api/profile');
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    if (profile) setForm(toForm(profile));
  }, [profile]);
  const changed = useMemo(() => {
    if (!profile || !form) return {};
    const base = toForm(profile);
    return Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== base[k as keyof Form]));
  }, [profile, form]);

  if (error) return <Notice msg={{ ok: false, text: error }} />;
  if (!profile || !form) return <Spinner />;
  const locked = profile.identityLocked;
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const p = await api.patch<Profile>('/api/profile', changed);
      setData(p);
      setMsg({ ok: true, text: 'Details saved' });
      void loadMe();
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(false);
    }
  };
  const lockIcon = locked ? <AIcon name="lock" size={13} className="text-muted" /> : null;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)]">
      <Card>
        <form onSubmit={save}>
          <H2>Your details</H2>
          <div className="mt-8 grid gap-x-6 gap-y-7 md:grid-cols-2">
            <Field label={<>First name {lockIcon}</>} htmlFor="pi-first">
              <input id="pi-first" className="field" autoComplete="given-name" readOnly={locked} value={form.firstName} onChange={set('firstName')} />
            </Field>
            <Field label={<>Last name {lockIcon}</>} htmlFor="pi-last">
              <input id="pi-last" className="field" autoComplete="family-name" readOnly={locked} value={form.lastName} onChange={set('lastName')} />
            </Field>
            {contactHidden ? (
              <HiddenContact id="pi-email" label="Email" value={profile.email} className="md:col-span-2" hint="Only the client and staff allowed to see contact details see this in full." />
            ) : (
              <EmailChange
                current={profile.email}
                onDone={(p) => {
                  setData(p);
                  setMsg({ ok: true, text: `Your email is now ${p.email}` });
                  void loadMe();
                }}
              />
            )}
            {contactHidden ? (
              <HiddenContact id="pi-phone" label="Phone" value={[profile.phoneCc, profile.phone].filter(Boolean).join(' ')} />
            ) : (
              <Field label="Phone" htmlFor="pi-phone">
                <div className="flex gap-3">
                  <select aria-label="Country code" className="field num !w-[120px] shrink-0" value={form.phoneCc} onChange={set('phoneCc')}>
                    <option value="">Code</option>
                    {PHONE_CODES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <input id="pi-phone" className="field num" inputMode="tel" autoComplete="tel-national" value={form.phone} onChange={set('phone')} />
                </div>
              </Field>
            )}
            <Field label={<>Date of birth {lockIcon}</>} htmlFor="pi-dob">
              <DateField
                id="pi-dob"
                className="field"
                buttonClassName="grid w-[52px] flex-none place-items-center rounded-[12px] border border-line-strong bg-card text-muted hover:text-fg"
                autoComplete="bday"
                readOnly={locked}
                yearPicker
                max={todayIn()}
                value={form.dob}
                onChange={(v) => set('dob')({ target: { value: v } })}
              />
            </Field>
            <Field label="Country" htmlFor="pi-country">
              <select id="pi-country" className="field" autoComplete="country" value={form.country} onChange={set('country')}>
                <option value="">Choose a country</option>
                {countryNames.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="City" htmlFor="pi-city">
              <input id="pi-city" className="field" autoComplete="address-level2" value={form.city} onChange={set('city')} />
            </Field>
            <Field label="Address" htmlFor="pi-address" className="md:col-span-2">
              <input id="pi-address" className="field" autoComplete="street-address" value={form.addressLine} onChange={set('addressLine')} />
            </Field>
            <Field label="Postal code" htmlFor="pi-postal">
              <input id="pi-postal" className="field num" autoComplete="postal-code" value={form.postalCode} onChange={set('postalCode')} />
            </Field>
            <Field label="Preferred language" htmlFor="pi-lang">
              <select id="pi-lang" className="field" value={form.language} onChange={set('language')}>
                {LANGUAGES.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Timezone" htmlFor="pi-tz" className="md:col-span-2" hint="Used for statements and dates in emails. Swap is always charged at a fixed UTC time.">
              <select id="pi-tz" className="field" value={form.timezone} onChange={set('timezone')}>
                {(timezones.includes(form.timezone) ? timezones : [form.timezone, ...timezones]).map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-8">
            <Notice msg={msg} onClose={() => setMsg(null)} />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:flex sm:justify-end">
            <PillButton kind="secondary" onClick={() => setForm(toForm(profile))} disabled={!Object.keys(changed).length}>
              Discard
            </PillButton>
            <PillButton type="submit" busy={busy} disabled={!Object.keys(changed).length}>
              Save changes
            </PillButton>
          </div>
        </form>
      </Card>
      <Card className="flex flex-col gap-4">
        <span className="grid size-12 place-items-center rounded-full bg-track">
          <AIcon name="lock" />
        </span>
        <H2 className="!text-[22px]">Name and date of birth</H2>
        <p className="m-0 text-[16px] leading-relaxed text-muted">
          {profile.kycStatus === 'approved' || profile.kycStatus === 'partial'
            ? 'These match your verified ID and can no longer be edited here. Contact support to change them.'
            : profile.kycStatus === 'pending'
              ? 'These are locked while we review your documents.'
              : 'These lock once you submit your documents. Make sure they match your ID exactly.'}
        </p>
        <PillLink to="/account/support" kind="secondary" className="w-max">
          Contact support
        </PillLink>
      </Card>
    </div>
  );
}
